import { __assign, __spreadArray } from "tslib";
import { VeltService } from './velt';
import { Constants } from './constants';
var EditorService = /** @class */ (function () {
    function EditorService(plugin) {
        this.getSelectedText = function (editor) {
            var _a;
            try {
                // Ensure there's a selection
                if (!((_a = editor.state) === null || _a === void 0 ? void 0 : _a.selection))
                    return null;
                // Extract and return the selected text
                return editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to);
            }
            catch (err) {
                VeltService.catchError('Error getting selected text:', err);
                return null;
            }
        };
        this.plugin = plugin;
    }
    // Helper function to find parent node with a specific ID pattern
    EditorService.prototype.findParentNodeWithId = function (editor) {
        try {
            var from = editor.state.selection.from;
            var domFrom = editor.view.domAtPos(from);
            var fromNode = domFrom.node;
            var editorDOM = editor.view.dom;
            var MAX_ITERATIONS = 100;
            var iterationCount = 0;
            var currentNode = fromNode;
            while (currentNode && currentNode !== document.body && iterationCount < MAX_ITERATIONS) {
                if (!editorDOM.contains(currentNode)) {
                    return null;
                }
                if (currentNode.id) {
                    return currentNode;
                }
                currentNode = currentNode.parentElement;
                iterationCount++;
            }
            return null;
        }
        catch (error) {
            return null;
        }
    };
    EditorService.prototype.onTransaction = function (_a) {
        var editor = _a.editor, name = _a.name;
        try {
            var changedAnnotationContent = new Map();
            // Phase 1: Collect all annotations in the document
            var annotationsToCheck = this.collectDocumentAnnotations(editor, name);
            // Phase 2: Process each annotation to check for changes
            for (var _i = 0, _b = Array.from(annotationsToCheck.entries()); _i < _b.length; _i++) {
                var _c = _b[_i], annotationId = _c[0], data = _c[1];
                var changes = this.processAnnotationChanges(editor, data);
                if (changes) {
                    changedAnnotationContent.set(annotationId, changes);
                }
            }
            // Phase 3: Apply updates for any annotations that changed
            if (changedAnnotationContent.size > 0) {
                var commentElement = VeltService.getCommentElement();
                if (!commentElement)
                    return;
                this.updateVeltComments(commentElement, changedAnnotationContent, editor);
            }
        }
        catch (error) {
            VeltService.catchError('Error on transaction:', error);
        }
    };
    /**
     * Collects all annotations present in the document for processing.
     * This function traverses the document tree and collects all text nodes that have
     * the specified mark type, organizing them by their annotation IDs.
     *
     * @param editor - The Tiptap editor instance to traverse
     * @param markType - The type of mark to look for (e.g., 'velt-comment')
     * @param annotationStore - An object with a get method to retrieve annotation data by ID
     *
     * @returns A Map where:
     * - Key: annotation ID (string)
     * - Value: AnnotationToCheck object containing:
     *   - id: The annotation ID
     *   - multiThreadAnnotationId: Optional ID for multi-thread annotations
     *   - annotation: The full annotation data from the store
     *   - nodes: Array of text nodes with their positions and content
     *   - originalText: The original text content of the annotation
     *   - originalOccurrence: The original occurrence number
     *   - targetTextNodeId: The ID of the target text node
     *
     * @example
     * ```typescript
     * const annotations = collectDocumentAnnotations(editor, 'velt-comment', annotationStore);
     * // Returns: Map<string, AnnotationToCheck>
     * ```
     *
     * @remarks
     * - Only processes text nodes that have the specified mark type
     * - Skips nodes without valid annotation IDs
     * - Groups multiple occurrences of the same annotation together
     * - Preserves original text and occurrence information for change detection
     */
    EditorService.prototype.collectDocumentAnnotations = function (editor, markType) {
        var _this = this;
        try {
            // Initialize a Map to store annotations grouped by their IDs
            var annotationsToCheck_1 = new Map();
            // Traverse all nodes in the document
            editor.state.doc.descendants(function (node, pos) {
                var _a, _b, _c, _d, _e, _f;
                // Cast node to CustomNode type for type safety
                var customNode = node;
                // Skip non-text nodes
                if (!customNode.isText)
                    return;
                // Find the specific mark type we're looking for
                var commentMark = customNode.marks.find(function (mark) { return mark.type.name === markType; });
                if (!commentMark)
                    return;
                // Extract annotation ID from mark attributes
                var annotationId = commentMark.attrs['annotation-id'];
                if (typeof annotationId !== 'string')
                    return;
                // Get multi-thread annotation ID if it exists
                var multiThreadAnnotationId = commentMark.attrs['multi-thread-annotation-id'];
                // Retrieve previous annotation data from store
                var prevAnnotationData = _this.plugin.storeService.annotationStore.get(annotationId);
                if (!prevAnnotationData)
                    return;
                // Check if we've already processed this annotation
                var existingAnnotation = annotationsToCheck_1.get(annotationId);
                // Prepare node data with text content and position
                var nodeData = {
                    node: { text: customNode.text || '', nodeSize: customNode.nodeSize },
                    pos: pos,
                };
                if (existingAnnotation) {
                    // If annotation exists, append this node to its nodes array
                    existingAnnotation.nodes.push(nodeData);
                }
                else {
                    // If it's a new annotation, create a new entry with initial data
                    annotationsToCheck_1.set(annotationId, {
                        id: annotationId,
                        multiThreadAnnotationId: multiThreadAnnotationId,
                        annotation: prevAnnotationData.annotation,
                        nodes: [nodeData],
                        originalText: ((_b = (_a = prevAnnotationData.annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig) === null || _b === void 0 ? void 0 : _b.text) || '',
                        originalOccurrence: ((_d = (_c = prevAnnotationData.annotation.context) === null || _c === void 0 ? void 0 : _c.textEditorConfig) === null || _d === void 0 ? void 0 : _d.occurrence) || 1,
                        targetTextNodeId: ((_f = (_e = prevAnnotationData.annotation.context) === null || _e === void 0 ? void 0 : _e.textEditorConfig) === null || _f === void 0 ? void 0 : _f.targetTextNodeId) || '',
                    });
                }
            });
            return annotationsToCheck_1;
        }
        catch (err) {
            return new Map();
        }
    };
    /**
     * Detects content changes in an annotation by comparing current and original text.
     * This function is used by the TiptapVeltComments extension to track text modifications
     * within annotated regions.
     *
     * @param nodes - Array of text nodes from the annotation
     * @param originalText - The original text content of the annotation
     * @returns Object containing:
     *   - currentText: The current text content after joining all nodes
     *   - contentChanged: Boolean indicating if text content has changed
     *
     * @example
     * ```typescript
     * const { currentText, contentChanged } = detectContentChanges(nodes, "Original text");
     * if (contentChanged) {
     *   console.log(`Text changed from "Original text" to "${currentText}"`);
     * }
     * ```
     */
    EditorService.prototype.detectContentChanges = function (nodes, originalText) {
        // Create a copy of nodes array to avoid mutating the original
        var sortedNodes = __spreadArray([], nodes, true).sort(function (a, b) { return a.pos - b.pos; });
        // Join all node texts with proper null handling
        var currentText = sortedNodes.map(function (n) { return n.node.text || ''; }).join('');
        return {
            currentText: currentText,
            contentChanged: currentText !== originalText,
        };
    };
    /**
     * Detects container changes and updates search results accordingly.
     * This function is part of the TiptapVeltComments extension's change detection system,
     * responsible for tracking when annotations move between different DOM containers.
     *
     * @param editor - The Tiptap editor instance
     * @param params - Object containing:
     *   - currentText: The current text content to search for
     *   - targetTextNodeId: The ID of the current container node
     *   - nodes: Array of text nodes from the annotation
     * @returns Object containing:
     *   - targetTextNodeIdChanged: Boolean indicating if container has changed
     *   - newTargetTextNodeId: The ID of the new container (if changed)
     *   - searchResults: Array of text matches found in the container
     *
     * @example
     * ```typescript
     * const result = detectContainerChanges(editor, {
     *   currentText: "Example text",
     *   targetTextNodeId: "container-1",
     *   nodes: [...]
     * });
     * if (result.targetTextNodeIdChanged) {
     *   console.log(`Annotation moved to container: ${result.newTargetTextNodeId}`);
     * }
     * ```
     */
    EditorService.prototype.detectContainerChanges = function (editor, params) {
        var annotationId = params.annotationId, currentText = params.currentText, targetTextNodeId = params.targetTextNodeId, nodes = params.nodes;
        var targetTextNodeIdChanged = false;
        var newTargetTextNodeId = targetTextNodeId;
        var searchResults = [];
        // First try to find text in the current container
        var containerChangeResult = this.processContainerChanges(editor, {
            annotationId: annotationId,
            currentText: currentText,
            targetTextNodeId: targetTextNodeId,
            nodes: nodes,
        });
        if (containerChangeResult) {
            // Container has changed, update tracking variables
            targetTextNodeIdChanged = containerChangeResult.changed;
            newTargetTextNodeId = containerChangeResult.newId;
            searchResults = containerChangeResult.searchResults;
        }
        else {
            // No container change, search in current or document context
            searchResults = targetTextNodeId
                ? this.findTextInDomElement(editor, currentText, targetTextNodeId)
                : this.findTextInDocument(editor, currentText);
        }
        return {
            targetTextNodeIdChanged: targetTextNodeIdChanged,
            newTargetTextNodeId: newTargetTextNodeId,
            searchResults: searchResults,
        };
    };
    /**
     * Creates an AnnotationChange object from the detected changes.
     * This function is used by the TiptapVeltComments extension to format
     * change information for the Velt comment system.
     *
     * @param data - The original annotation data
     * @param changes - Object containing all detected changes:
     *   - currentText: The current text content
     *   - contentChanged: Boolean indicating text change
     *   - currentOccurrence: The new occurrence number
     *   - occurrenceChanged: Boolean indicating occurrence change
     *   - targetTextNodeIdChanged: Boolean indicating container change
     *   - newTargetTextNodeId: The new container ID
     * @returns A complete AnnotationChange object ready for Velt processing
     *
     * @example
     * ```typescript
     * const change = createAnnotationChange(annotationData, {
     *   currentText: "Updated text",
     *   contentChanged: true,
     *   currentOccurrence: 2,
     *   occurrenceChanged: true,
     *   targetTextNodeIdChanged: false,
     *   newTargetTextNodeId: "container-1"
     * });
     * ```
     */
    EditorService.prototype.createAnnotationChange = function (data, changes) {
        var currentText = changes.currentText, contentChanged = changes.contentChanged, currentOccurrence = changes.currentOccurrence, occurrenceChanged = changes.occurrenceChanged, targetTextNodeIdChanged = changes.targetTextNodeIdChanged, newTargetTextNodeId = changes.newTargetTextNodeId;
        // Create the change object with all necessary information
        return {
            annotationId: data.id,
            multiThreadAnnotationId: data.multiThreadAnnotationId,
            originalText: data.originalText,
            currentText: contentChanged ? currentText : data.originalText,
            originalOccurrence: data.originalOccurrence,
            currentOccurrence: currentOccurrence,
            originalTargetTextNodeId: data.targetTextNodeId,
            newTargetTextNodeId: targetTextNodeIdChanged ? newTargetTextNodeId : data.targetTextNodeId,
            annotation: data.annotation,
            contentChanged: contentChanged,
            occurrenceChanged: occurrenceChanged,
            targetTextNodeIdChanged: targetTextNodeIdChanged,
        };
    };
    /**
     * Processes a single annotation to detect changes in content, occurrence, or container.
     * This function is a critical part of the TiptapVeltComments extension, responsible for
     * detecting and tracking changes in annotated text within the editor.
     *
     * The function works in conjunction with the TiptapVeltComments extension to:
     * 1. Track text modifications within annotations
     * 2. Detect when annotations move between different containers
     * 3. Update occurrence numbers when text is duplicated or moved
     * 4. Maintain synchronization between Tiptap and Velt's comment system
     *
     * @param editor - The Tiptap editor instance
     * @param data - The annotation data to process
     * @returns An AnnotationChange object if changes are detected, null otherwise
     *
     * @throws {Error} When invalid input data is provided
     * @throws {Error} When editor operations fail
     *
     * @example
     * ```typescript
     * // Inside TiptapVeltComments extension
     * const changes = processAnnotationChanges(editor, annotationData);
     * if (changes) {
     *   // Update Velt comment system with changes
     *   updateVeltComments(commentElement, new Map([[changes.annotationId, changes]]));
     * }
     * ```
     *
     * @remarks
     * - This function is called during editor transactions
     * - Changes are detected by comparing current state with original state
     * - Three types of changes are tracked: content, occurrence, and container
     * - The function is part of the TiptapVeltComments extension's change detection system
     */
    EditorService.prototype.processAnnotationChanges = function (editor, data) {
        try {
            // Input validation
            if (!editor || !data) {
                throw new Error('Invalid input: editor and data are required');
            }
            // Early return if required data is missing
            if (!data.originalText || !data.annotation) {
                return null;
            }
            // Type guard for nodes array
            if (!Array.isArray(data.nodes) || data.nodes.length === 0) {
                return null;
            }
            // Step 1: Detect content changes by comparing current and original text
            var _a = this.detectContentChanges(data.nodes, data.originalText), currentText = _a.currentText, contentChanged = _a.contentChanged;
            // Step 2: Detect container changes and get updated search results
            var _b = this.detectContainerChanges(editor, {
                annotationId: data.id,
                currentText: currentText,
                targetTextNodeId: data.targetTextNodeId,
                nodes: data.nodes,
            }), targetTextNodeIdChanged = _b.targetTextNodeIdChanged, newTargetTextNodeId = _b.newTargetTextNodeId, searchResults = _b.searchResults;
            // Step 3: Calculate occurrence changes based on search results
            var currentOccurrence = this.calculateNewOccurrence(editor, {
                searchResults: searchResults,
                nodes: data.nodes,
                contentChanged: contentChanged,
                currentText: currentText,
                targetTextNodeId: targetTextNodeIdChanged ? newTargetTextNodeId : data.targetTextNodeId,
                originalOccurrence: data.originalOccurrence,
            });
            var occurrenceChanged = currentOccurrence !== data.originalOccurrence;
            // Step 4: Create and return change object if any changes detected
            if (contentChanged || occurrenceChanged || targetTextNodeIdChanged) {
                return this.createAnnotationChange(data, {
                    currentText: currentText,
                    contentChanged: contentChanged,
                    currentOccurrence: currentOccurrence,
                    occurrenceChanged: occurrenceChanged,
                    targetTextNodeIdChanged: targetTextNodeIdChanged,
                    newTargetTextNodeId: newTargetTextNodeId,
                });
            }
            return null;
        }
        catch (err) {
            return null;
        }
    };
    EditorService.prototype.processContainerChanges = function (editor, params) {
        var annotationId = params.annotationId, currentText = params.currentText, targetTextNodeId = params.targetTextNodeId;
        if (!targetTextNodeId)
            return null;
        // Find node with annotationId from editor.state.doc
        var foundNodePos = null;
        editor.state.doc.descendants(function (node, pos) {
            var mark = node.marks.find(function (m) { return m.type.name === 'tiptapVeltComments' && m.attrs['annotation-id'] === annotationId; });
            if (mark && !foundNodePos) {
                foundNodePos = pos;
            }
        });
        if (foundNodePos !== null) {
            // Get DOM position for the found node
            var domPos = editor.view.domAtPos(foundNodePos);
            var currentNode = domPos.node;
            var editorDom = editor.view.dom;
            // Traverse up to find parent with id
            while (currentNode && !currentNode.id && currentNode !== document.body) {
                currentNode = currentNode.parentElement || currentNode;
                // Check if currentNode is outside of the editor using contains()
                if (!editorDom.contains(currentNode)) {
                    break;
                }
            }
            if ((currentNode === null || currentNode === void 0 ? void 0 : currentNode.id) && currentNode.id !== targetTextNodeId) {
                var searchResults = this.findTextInDomElement(editor, currentText, currentNode.id);
                return {
                    changed: true,
                    newId: currentNode.id,
                    searchResults: searchResults,
                };
            }
        }
        return null;
    };
    /**
     * Calculates the new occurrence number for an annotation based on its position in the document.
     * This function is used to determine which instance of a repeated text should be highlighted
     * when an annotation spans multiple occurrences of the same text.
     *
     * @param editor - The Tiptap editor instance
     * @param params - Object containing:
     *   - searchResults: Array of text matches found in the document
     *   - nodes: Array of text nodes from the annotation with their positions
     *   - contentChanged: Boolean indicating if the text content has changed
     *   - currentText: The current text content to search for
     *   - targetTextNodeId: The ID of the container node (if any)
     *   - originalOccurrence: The original occurrence number before changes
     * @returns The new occurrence number (1-based index) or the original occurrence if no match found
     *
     * @example
     * ```typescript
     * const newOccurrence = calculateNewOccurrence(editor, {
     *   searchResults: [{ from: 0, to: 10 }, { from: 20, to: 30 }],
     *   nodes: [{ node: { text: "example" }, pos: 25 }],
     *   contentChanged: false,
     *   currentText: "example",
     *   targetTextNodeId: "container-1",
     *   originalOccurrence: 1
     * });
     * // Returns: 2 (second occurrence)
     * ```
     *
     * @remarks
     * - The function uses a 1-based index for occurrence numbers
     * - If no matches are found, returns the original occurrence number
     * - Handles cases where annotation spans multiple text nodes
     * - Considers both the start and end positions of the annotation
     */
    EditorService.prototype.calculateNewOccurrence = function (editor, params) {
        // Return original occurrence if no search results found
        if (params.searchResults.length === 0) {
            return params.originalOccurrence;
        }
        // Calculate the range of the current annotation
        var firstNode = params.nodes[0];
        var lastNode = params.nodes[params.nodes.length - 1];
        var annotationRange = {
            from: firstNode.pos,
            to: lastNode.pos + (lastNode.node.nodeSize || 0),
        };
        // Iterate through search results to find the matching occurrence
        for (var i = 0; i < params.searchResults.length; i++) {
            var result = params.searchResults[i];
            // Check if the current search result overlaps with our annotation range
            // This handles three cases:
            // 1. Search result starts before annotation and ends within it
            // 2. Search result starts within annotation and ends after it
            // 3. Search result is completely contained within annotation
            if ((result.from <= annotationRange.from && result.to >= annotationRange.from) ||
                (result.from <= annotationRange.to && result.to >= annotationRange.to) ||
                (annotationRange.from <= result.from && annotationRange.to >= result.to)) {
                // Return 1-based index of the matching occurrence
                return i + 1;
            }
        }
        // If no matching occurrence found, return the original occurrence
        return params.originalOccurrence;
    };
    /**
     * Updates the Velt comment element with new annotation data
     */
    EditorService.prototype.updateVeltComments = function (commentElement, changes, editor) {
        var _a;
        try {
            for (var _i = 0, _b = Array.from(changes.entries()); _i < _b.length; _i++) {
                var _c = _b[_i], annotationId = _c[0], changeData = _c[1];
                try {
                    if (!((_a = changeData.annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig))
                        continue;
                    var textEditorConfig = JSON.parse(JSON.stringify(changeData.annotation.context.textEditorConfig));
                    if (changeData.contentChanged) {
                        textEditorConfig.text = changeData.currentText;
                    }
                    if (changeData.occurrenceChanged) {
                        textEditorConfig.occurrence = changeData.currentOccurrence;
                    }
                    if (changeData.targetTextNodeIdChanged) {
                        textEditorConfig.targetTextNodeId = changeData.newTargetTextNodeId;
                    }
                    var context = __assign(__assign({}, changeData.annotation.context), { textEditorConfig: textEditorConfig });
                    // Remove data from store
                    this.plugin.storeService.annotationStore.remove(annotationId);
                    this.plugin.editorService.removeVeltCommentFromEditor(editor, annotationId);
                    VeltService.updateContext({ annotationId: annotationId, context: context });
                }
                catch (err) {
                    // catchError('Error updating Velt comments', err);
                }
            }
        }
        catch (err) {
            // catchError('Error updating Velt comments', err);
        }
    };
    /**
     * Implements KMP (Knuth-Morris-Pratt) pattern matching algorithm
     * @param pattern The pattern to search for
     * @returns The longest proper prefix array
     */
    EditorService.prototype.computeKMPTable = function (pattern) {
        try {
            var lps = new Array(pattern.length).fill(0);
            var len = 0;
            var i = 1;
            while (i < pattern.length) {
                if (pattern[i] === pattern[len]) {
                    len++;
                    lps[i] = len;
                    i++;
                }
                else {
                    if (len !== 0) {
                        len = lps[len - 1];
                    }
                    else {
                        lps[i] = 0;
                        i++;
                    }
                }
            }
            return lps;
        }
        catch (error) {
            return [];
        }
    };
    /**
     * Finds all occurrences of a pattern in text using KMP algorithm with early termination
     * @param text The text to search in
     * @param pattern The pattern to search for
     * @param startPos The starting position offset
     * @param maxOccurrences Maximum number of occurrences to find (optional)
     * @returns Array of match positions
     */
    EditorService.prototype.kmpSearch = function (text, pattern, startPos, maxOccurrences) {
        if (startPos === void 0) { startPos = 0; }
        try {
            var positions = [];
            if (!pattern || !text || pattern.length > text.length)
                return positions;
            var lps = this.computeKMPTable(pattern);
            var i = 0;
            var j = 0;
            while (i < text.length) {
                if (pattern[j] === text[i]) {
                    i++;
                    j++;
                }
                if (j === pattern.length) {
                    positions.push(startPos + i - j);
                    j = lps[j - 1];
                    if (maxOccurrences && positions.length >= maxOccurrences) {
                        break;
                    }
                }
                else if (i < text.length && pattern[j] !== text[i]) {
                    if (j !== 0) {
                        j = lps[j - 1];
                    }
                    else {
                        i++;
                    }
                }
            }
            return positions;
        }
        catch (error) {
            return [];
        }
    };
    /**
     * Find text within a specific DOM element by its ID using KMP algorithm with early termination
     */
    EditorService.prototype.findTextInDomElement = function (editor, text, domElementId, desiredOccurrence) {
        var _a;
        try {
            var matches = [];
            var domNode = document.getElementById(domElementId);
            if (!domNode) {
                return matches;
            }
            var editorView = editor.view;
            var posFromDOM = editorView.posAtDOM(domNode, 0);
            if (posFromDOM === null) {
                return matches;
            }
            var $pos = editor.state.doc.resolve(posFromDOM);
            var nodePos = $pos.pos;
            var nodeSize = ((_a = domNode.textContent) === null || _a === void 0 ? void 0 : _a.length) || 0;
            // Collect all text nodes and their positions
            var textNodes_1 = [];
            editor.state.doc.nodesBetween(nodePos, nodePos + nodeSize, function (node, pos) {
                var customNode = node;
                if (customNode.isText) {
                    textNodes_1.push({
                        text: customNode.text || '',
                        pos: pos,
                    });
                }
            });
            // Combine all text nodes into a single string for searching
            var combinedText = textNodes_1.map(function (n) { return n.text; }).join('');
            var positions = this.kmpSearch(combinedText, text, 0, desiredOccurrence);
            // Convert positions in combined text back to document positions
            for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
                var pos = positions_1[_i];
                var currentPos = 0;
                var startNodeIndex = -1;
                var startOffset = 0;
                var endNodeIndex = -1;
                var endOffset = 0;
                // Find the start and end nodes for this match
                for (var i = 0; i < textNodes_1.length; i++) {
                    var node = textNodes_1[i];
                    var nodeLength = node.text.length;
                    // Check if this node contains the start of the match
                    if (startNodeIndex === -1 && pos < currentPos + nodeLength) {
                        startNodeIndex = i;
                        startOffset = pos - currentPos;
                    }
                    // Check if this node contains the end of the match
                    if (endNodeIndex === -1 && pos + text.length <= currentPos + nodeLength) {
                        endNodeIndex = i;
                        endOffset = pos + text.length - currentPos;
                        break;
                    }
                    currentPos += nodeLength;
                }
                // If we found both start and end nodes, create the match
                if (startNodeIndex !== -1 && endNodeIndex !== -1) {
                    var startPos = textNodes_1[startNodeIndex].pos + startOffset;
                    var endPos = textNodes_1[endNodeIndex].pos + endOffset;
                    matches.push({
                        from: startPos,
                        to: endPos,
                    });
                    if (desiredOccurrence && matches.length >= desiredOccurrence) {
                        break;
                    }
                }
            }
            return matches;
        }
        catch (error) {
            return [];
        }
    };
    /**
     * Helper function to find text in the document using KMP with early termination
     */
    EditorService.prototype.findTextInDocument = function (editor, text, desiredOccurrence) {
        try {
            var matches = [];
            var doc = editor.state.doc;
            var maxMatches = desiredOccurrence || undefined;
            // Collect all text nodes and their positions
            var textNodes_2 = [];
            doc.descendants(function (node, pos) {
                var customNode = node;
                if (customNode.isText) {
                    textNodes_2.push({
                        text: customNode.text || '',
                        pos: pos,
                    });
                }
            });
            // Combine all text nodes into a single string for searching
            var combinedText = textNodes_2.map(function (n) { return n.text; }).join('');
            var positions = this.kmpSearch(combinedText, text, 0, maxMatches);
            // Convert positions in combined text back to document positions
            for (var _i = 0, positions_2 = positions; _i < positions_2.length; _i++) {
                var pos = positions_2[_i];
                var currentPos = 0;
                var startNodeIndex = -1;
                var startOffset = 0;
                var endNodeIndex = -1;
                var endOffset = 0;
                // Find the start and end nodes for this match
                for (var i = 0; i < textNodes_2.length; i++) {
                    var node = textNodes_2[i];
                    var nodeLength = node.text.length;
                    // Check if this node contains the start of the match
                    if (startNodeIndex === -1 && pos < currentPos + nodeLength) {
                        startNodeIndex = i;
                        startOffset = pos - currentPos;
                    }
                    // Check if this node contains the end of the match
                    if (endNodeIndex === -1 && pos + text.length <= currentPos + nodeLength) {
                        endNodeIndex = i;
                        endOffset = pos + text.length - currentPos;
                        break;
                    }
                    currentPos += nodeLength;
                }
                // If we found both start and end nodes, create the match
                if (startNodeIndex !== -1 && endNodeIndex !== -1) {
                    var startPos = textNodes_2[startNodeIndex].pos + startOffset;
                    var endPos = textNodes_2[endNodeIndex].pos + endOffset;
                    matches.push({
                        from: startPos,
                        to: endPos,
                    });
                    if (maxMatches && matches.length >= maxMatches) {
                        break;
                    }
                }
            }
            return matches;
        }
        catch (error) {
            return [];
        }
    };
    /**
     * Find the occurrence index of selected text
     * @param editor The Tiptap editor instance
     * @param selectedText The text to find occurrences for
     * @param targetTextNode Target text node for scoped searching (optional)
     * @param selectionFrom The from position of current selection
     * @param selectionTo The to position of current selection
     * @returns The 1-indexed occurrence number
     */
    EditorService.prototype.findOccurrenceIndex = function (editor, selectedText, targetTextNode, selectionFrom, selectionTo) {
        try {
            if (!selectedText.trim()) {
                return 1;
            }
            var matches = [];
            if (targetTextNode && targetTextNode.id) {
                matches = this.findTextInDomElement(editor, selectedText, targetTextNode.id);
            }
            else {
                matches = this.findTextInDocument(editor, selectedText);
            }
            for (var i = 0; i < matches.length; i++) {
                if (matches[i].from === selectionFrom && matches[i].to === selectionTo) {
                    return i + 1;
                }
            }
            return 1;
        }
        catch (error) {
            return 1;
        }
    };
    /**
     * Highlights text in the editor with a Velt comment
     * @param editor The editor instance
     * @param textToFind The text to highlight
     * @param commentOptions Options for the comment
     */
    EditorService.prototype.highlightTextWithVeltComment = function (editor, textToFind, commentOptions) {
        var _a, _b, _c, _d, _e, _f;
        try {
            var targetTextNodeId = commentOptions.targetTextNodeId;
            var desiredOccurrence = (commentOptions === null || commentOptions === void 0 ? void 0 : commentOptions.occurrence) || 1;
            // const locationId = commentOptions?.originalAnnotation?.location?.id;
            // check if newTextEditorConfig is different from stored annotation textEditorConfig
            if (commentOptions.annotationId) {
                var storedData = this.plugin.storeService.annotationStore.get(commentOptions.annotationId);
                if (storedData) {
                    var storedConfig = (_a = storedData.annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig;
                    var newConfig = (_c = (_b = commentOptions.originalAnnotation) === null || _b === void 0 ? void 0 : _b.context) === null || _c === void 0 ? void 0 : _c.textEditorConfig;
                    if (storedConfig &&
                        newConfig &&
                        (storedConfig.text !== newConfig.text ||
                            storedConfig.occurrence !== newConfig.occurrence ||
                            storedConfig.targetTextNodeId !== newConfig.targetTextNodeId)) {
                        this.removeVeltCommentFromEditor(editor, commentOptions.annotationId);
                        // return;
                    }
                }
            }
            if (!textToFind || textToFind.length === 0) {
                return;
            }
            if (targetTextNodeId) {
                var domNode = document.getElementById(targetTextNodeId);
                if (domNode) {
                    var locationId = (_d = domNode
                        .closest("[".concat(Constants.ATTRIBUTES.LOCATION_ID, "]"))) === null || _d === void 0 ? void 0 : _d.getAttribute(Constants.ATTRIBUTES.LOCATION_ID);
                    if (locationId !== ((_f = (_e = commentOptions.originalAnnotation) === null || _e === void 0 ? void 0 : _e.location) === null || _f === void 0 ? void 0 : _f.id)) {
                        return;
                    }
                }
            }
            var matches = [];
            if (targetTextNodeId) {
                matches = this.findTextInDomElement(editor, textToFind, targetTextNodeId, desiredOccurrence);
            }
            else {
                matches = this.findTextInDocument(editor, textToFind, desiredOccurrence);
            }
            if (matches.length === 0) {
                return;
            }
            var occurrence = Math.min(desiredOccurrence, matches.length);
            var matchToConsider = matches[occurrence - 1];
            if (matchToConsider && commentOptions.annotationId) {
                this.plugin.storeService.annotationStore.set(commentOptions.annotationId, {
                    annotation: commentOptions.originalAnnotation || {
                        annotationId: commentOptions.annotationId,
                        multiThreadAnnotationId: commentOptions.multiThreadAnnotationId,
                        text: textToFind,
                    },
                    pluginReference: {
                        node: document.createElement('velt-comment-text'),
                        position: { from: matchToConsider.from, to: matchToConsider.to },
                    },
                });
                editor
                    .chain()
                    .focus()
                    .setVeltComment(commentOptions.annotationId, commentOptions.multiThreadAnnotationId, matchToConsider.from, matchToConsider.to)
                    .run();
            }
        }
        catch (error) {
            // Error handling is done through catchError in the original code
        }
    };
    /**
     * Removes a Velt comment mark from the document
     * @param editor - The Tiptap editor instance
     * @param annotationId - The ID of the annotation to remove
     * @returns boolean - True if the annotation was successfully removed, false otherwise
     * @throws Error if the stored data is invalid or if the mark type is not found
     */
    EditorService.prototype.removeVeltCommentFromEditor = function (editor, annotationId) {
        var _a;
        try {
            // Validate input
            if (!editor || !annotationId) {
                throw new Error('Invalid input: editor and annotationId are required');
            }
            // Get stored data and validate
            var storedData = this.plugin.storeService.annotationStore.get(annotationId);
            if (!((_a = storedData === null || storedData === void 0 ? void 0 : storedData.pluginReference) === null || _a === void 0 ? void 0 : _a.position)) {
                throw new Error('Invalid stored data for annotation');
            }
            var _b = storedData.pluginReference.position, from_1 = _b.from, to_1 = _b.to;
            if (from_1 >= to_1) {
                throw new Error('Invalid position range: from must be less than to');
            }
            // Get mark type once for better performance
            var markType_1 = editor.schema.marks.tiptapVeltComments;
            if (!markType_1) {
                throw new Error('Velt comment mark type not found');
            }
            // Track if we found and removed any marks
            var marksRemoved_1 = false;
            // Use a single transaction for all mark removals
            editor
                .chain()
                .focus()
                .command(function (_a) {
                var tr = _a.tr;
                // Use descendants for better performance with text nodes
                editor.state.doc.descendants(function (node, pos) {
                    // Skip if position is outside our range
                    if (pos < from_1 || pos > to_1)
                        return;
                    // Find the specific mark for this annotation
                    var mark = node.marks.find(function (m) { return m.type === markType_1 && m.attrs['annotation-id'] === annotationId; });
                    if (mark) {
                        tr.removeMark(pos, pos + node.nodeSize, markType_1);
                        marksRemoved_1 = true;
                    }
                });
                return marksRemoved_1;
            })
                .run();
            return marksRemoved_1;
        }
        catch (error) {
            // Use the existing error handling utility
            // catchError('Error removing Velt comment', error);
            return false;
        }
    };
    EditorService.getEditorId = function (editor) {
        var _a, _b, _c, _d;
        try {
            if (!editor)
                return null;
            var editorId = (_b = (_a = editor.storage) === null || _a === void 0 ? void 0 : _a.tiptapVeltComments) === null || _b === void 0 ? void 0 : _b.editorId;
            if (editorId)
                return editorId;
            // Get the DOM node representing the editor
            var domNode = editor.view.dom;
            if (domNode) {
                return ((_d = (_c = domNode === null || domNode === void 0 ? void 0 : domNode.closest("[".concat(Constants.ATTRIBUTES.EDITOR_ID, "]"))) === null || _c === void 0 ? void 0 : _c.getAttribute(Constants.ATTRIBUTES.EDITOR_ID)) !== null && _d !== void 0 ? _d : null);
            }
            // No parent with data-editor-id found
            return null;
        }
        catch (error) {
            // Handle cases where the editor might not be connected to the DOM
            VeltService.catchError('Error finding editor ID:', error);
            return null;
        }
    };
    return EditorService;
}());
export { EditorService };
